import TimeService from 'dashboard-services/services/TimeService'
import { DataStorageEnums } from 'dashboard-services/api/dataStorage'

import dayjs from 'dayjs'

import FormulaResolver from 'ng-formula-resolver/resolver'

import basicCustomInsightConsts from 'actions/modules/insight/custom/basicConsts'

export default (() => {
  const { COLUMN_TYPES, CUSTOM_DATE_RANGES, INPUT_PARAMS_DATE_RANGE_TYPES, INPUT_PARAMS_SINGLE_DATE_RANGE_TYPES, DATE_PRESET_FORMULA_COLUMN_IDS } = basicCustomInsightConsts,
        PERIOD_MONTHS = {
          [INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_MONTH]: 1,
          [INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_MONTH]: 1,
          [INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_QUARTER]: 3,
          [INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_QUARTER]: 3,
          [INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_TRIMESTER]: 4,
          [INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_TRIMESTER]: 4,
          [INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_YEAR]: 12,
          [INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_YEAR]: 12
        },
        PREVIOUS_PERIOD_TYPES = new Set([
          INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_MONTH,
          INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_QUARTER,
          INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_TRIMESTER,
          INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_YEAR
        ])

  const getDate = ({ value }) => {
    if(typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value) || !dayjs(value).isValid()) {
      return
    }
    const date = value.slice(0, 10)
    return dayjs(date).format(TimeService.ISO_DATE_FORMAT) === date ? date : undefined
  }

  const getFormulaDates = ({ result }) => {
    if(Object.keys(result?.errorsByRowId?.[0] || {}).length) {
      return { errorsByRowId: result.errorsByRowId }
    }
    const row = result?.rows?.[0],
          fromDate = getDate({ value: row?.[DATE_PRESET_FORMULA_COLUMN_IDS.FROM] }),
          toDate = getDate({ value: row?.[DATE_PRESET_FORMULA_COLUMN_IDS.TO] })

    if(fromDate && toDate) {
      return { fromDate, toDate }
    }

    return { errorsByRowId: { 0: {
      ...(!fromDate ? { [DATE_PRESET_FORMULA_COLUMN_IDS.FROM]: 'The formula must return a valid date.' } : {}),
      ...(!toDate ? { [DATE_PRESET_FORMULA_COLUMN_IDS.TO]: 'The formula must return a valid date.' } : {})
    } } }
  }

  const getFormulaError = ({ error }) => ({ errorsByRowId: { 0: { [DATE_PRESET_FORMULA_COLUMN_IDS.FROM]: error?.message || 'Unable to calculate the formula.' } } })

  const getFormulaParams = ({ fromFormula, toFormula, parser }) => ({
    columns: [DATE_PRESET_FORMULA_COLUMN_IDS.FROM, ...(toFormula === undefined ? [] : [DATE_PRESET_FORMULA_COLUMN_IDS.TO])].map(columnId => ({ columnId })),
    columnsConfig: [
      { columnId: DATE_PRESET_FORMULA_COLUMN_IDS.FROM, columnType: COLUMN_TYPES.DYNAMIC, asyncCallType: INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA, columnAsyncFormula: fromFormula },
      ...(toFormula === undefined ? [] : [{ columnId: DATE_PRESET_FORMULA_COLUMN_IDS.TO, columnType: COLUMN_TYPES.DYNAMIC, asyncCallType: INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA, columnAsyncFormula: toFormula }])
    ],
    data: [{}],
    parser,
    expressionCache: new Map()
  })

  const resolveFormulaResult = ({ params, getQueryBuilder, getResult }) => {
    const result = FormulaResolver.resolveFormulaTableData(params),
          errors = Object.values(result.errorsByRowId?.[0] || {}),
          requiresAsyncEvaluation = errors.some(error => error.includes('only supported during async formula evaluation'))

    if(requiresAsyncEvaluation) {
      return FormulaResolver.resolveFormulaTableDataAsync({ ...params, getQueryBuilder })
        .then(result => getResult({ result }))
        .catch(error => getFormulaError({ error }))
    }
    return getResult({ result })
  }

  const resolveFormulaDates = ({ params, getQueryBuilder }) => resolveFormulaResult({ params, getQueryBuilder, getResult: getFormulaDates })

  const getFormulaDate = ({ result }) => {
    if(Object.keys(result?.errorsByRowId?.[0] || {}).length) {
      return { errorsByRowId: result.errorsByRowId }
    }
    const specificDate = getDate({ value: result?.rows?.[0]?.[DATE_PRESET_FORMULA_COLUMN_IDS.FROM] })
    return specificDate
      ? { specificDate }
      : { errorsByRowId: { 0: { [DATE_PRESET_FORMULA_COLUMN_IDS.FROM]: 'The formula must return a valid date.' } } }
  }

  const getFormulaDateRange = ({ preset, getQueryBuilder, parser, parserError }) => {
    const { fromFormula, formula, toFormula } = preset

    if(!(fromFormula ?? formula)?.trim() || !toFormula?.trim()) {
      return
    }
    if(!parser) {
      return { errorsByRowId: { 0: { [DATE_PRESET_FORMULA_COLUMN_IDS.FROM]: parserError?.message || 'Unable to initialize the formula parser.' } } }
    }
    try {
      const params = getFormulaParams({ fromFormula: fromFormula ?? formula, toFormula, parser })
      return resolveFormulaDates({ params, getQueryBuilder })
    } catch(error) {
      return getFormulaError({ error })
    }
  }

  const getPreviousBusinessDayDateRange = ({ preset, definedTime, getQueryBuilder, parser, parserError }) => {
    const { holidayCalendar } = preset,
          previousDate = dayjs(TimeService.getCurrentDate(definedTime)).subtract(1, 'day')

    if(typeof holidayCalendar !== 'string' || !holidayCalendar.trim()) {
      const weekendOffset = previousDate.day() === 0 ? 2 : previousDate.day() === 6 ? 1 : 0,
            date = previousDate.subtract(weekendOffset, 'day').format(TimeService.ISO_DATE_FORMAT)

      return { fromDate: date, toDate: date }
    }
    const businessDayFormula = `getLastBusinessDay(${JSON.stringify(previousDate.format(TimeService.ISO_DATE_FORMAT))}, ${JSON.stringify(holidayCalendar)})`

    return getFormulaDateRange({
      preset: { fromFormula: businessDayFormula, toFormula: businessDayFormula },
      getQueryBuilder,
      parser,
      parserError
    })
  }

  const getSpecificDateFormula = ({ formula, getQueryBuilder, parser, parserError }) => {
    if(!formula?.trim()) {
      return
    }
    if(!parser) {
      return { errorsByRowId: { 0: { [DATE_PRESET_FORMULA_COLUMN_IDS.FROM]: parserError?.message || 'Unable to initialize the formula parser.' } } }
    }
    try {
      const params = getFormulaParams({ fromFormula: formula, parser })
      return resolveFormulaResult({ params, getQueryBuilder, getResult: getFormulaDate })
    } catch(error) {
      return getFormulaError({ error })
    }
  }

  const getPeriodDateRange = ({ dateType, today }) => {
    const months = PERIOD_MONTHS[dateType]

    if(!months) {
      return
    }

    const from = today.startOf('year')
      .add(Math.floor(today.month() / months) * months, 'month')
      .subtract(PREVIOUS_PERIOD_TYPES.has(dateType) ? months : 0, 'month')

    return { fromDate: from.format(TimeService.ISO_DATE_FORMAT), toDate: from.add(months, 'month').subtract(1, 'day').format(TimeService.ISO_DATE_FORMAT) }
  }

  const getSpecificDate = ({ preset, definedTime, getQueryBuilder, parser, parserError }) => {
    const { dateType } = preset

    switch(dateType) {
      case INPUT_PARAMS_DATE_RANGE_TYPES.TODAY: {
        const today = dayjs(TimeService.getCurrentDate(definedTime))
        return { specificDate: today.format(TimeService.ISO_DATE_FORMAT) }
      }
      case INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_BUSINESS_DAY: {
        const dates = getPreviousBusinessDayDateRange({ preset, definedTime, getQueryBuilder, parser, parserError }),
              getSpecificDateResult = result => result?.errorsByRowId ? result : result?.fromDate ? { specificDate: result.fromDate } : undefined
        return dates?.then ? dates.then(getSpecificDateResult) : getSpecificDateResult(dates)
      }
      case INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA:
        return getSpecificDateFormula({ formula: preset.formula ?? preset.fromFormula, getQueryBuilder, parser, parserError })
      default:
        return undefined
    }
  }

  const getPresetDateRange = ({ preset, definedTime, getQueryBuilder, parser, parserError, dataRange = DataStorageEnums.DATA_RANGE.BETWEEN }) => {
    const { dateType } = preset

    if(dataRange === CUSTOM_DATE_RANGES.SPECIFIC_DATE.value) {
      return INPUT_PARAMS_SINGLE_DATE_RANGE_TYPES.includes(dateType)
        ? getSpecificDate({ preset, definedTime, getQueryBuilder, parser, parserError })
        : undefined
    }
    if(dataRange !== DataStorageEnums.DATA_RANGE.BETWEEN) {
      return undefined
    }

    switch(dateType) {
      case INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_BUSINESS_DAY:
        return getPreviousBusinessDayDateRange({ preset, definedTime, getQueryBuilder, parser, parserError })
      case INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA:
        return getFormulaDateRange({ preset, getQueryBuilder, parser, parserError })
      case INPUT_PARAMS_DATE_RANGE_TYPES.TODAY: {
        const today = dayjs(TimeService.getCurrentDate(definedTime))
        return { fromDate: today.format(TimeService.ISO_DATE_FORMAT), toDate: today.format(TimeService.ISO_DATE_FORMAT) }
      }
      default:
        return getPeriodDateRange({ dateType, today: dayjs(TimeService.getCurrentDate(definedTime)) })
    }
  }

  return { getPresetDateRange }
})()
